// MapPreviewQueueService.js — single root-level OL Map + MapPreviewEngine shared by every
// map-node preview in a report, replacing the old one-engine-per-node model.
//
// Why: a report with N map-nodes used to spin up N live OpenLayers Maps (N canvases, N sets of
// tile/style fetches) purely to render off-screen and capture one PNG snapshot each. This service
// owns exactly one hidden Map + MapPreviewEngine and processes refresh requests through a FIFO
// job queue (keyed by nodeId), so at most one preview render is ever in flight at a time.
//
// This is also where the mil-sym icon renderer gets initialized (`ensureInit()`), once, at
// construction — previously `RendererSettingsProvider` only called it when the full-screen
// MapEditorModal mounted, so any map-node preview rendered before that modal was ever opened in
// the session would silently bake-in icon-less fallback shapes (permanently, since a snapshot is
// a one-shot capture). Centralizing ensureInit() here guarantees the renderer is ready before the
// very first queued job runs, regardless of whether the editor modal was ever opened.
//
// Ticket 05: a job whose capture fails (either `captureSnapshot()` yielding no `dataUrl`, or an
// exception anywhere in `_runJob`) is no longer swallowed silently — see `getJobState`'s `'error'`
// state and `getJobError()`.
import { MapPreviewEngine } from './MapPreviewEngine';
import { ensureInit } from './editor/renderer/SymbolRenderer';
import { expandTacticalMarkingsToFeatures } from './editor/renderer/markingStyle';
import { readMapPreviewState } from './MapFragmentStore';
import {
    markingsToGeoJSON,
    resolveActiveViewId,
    resolveViewBbox,
    resolveViewFitPadding,
    viewFitKey,
} from './schema/mapPmAdapter';
import { setMapPreview, getMapPreviewSignature } from './mapPreviewsYMap';

// Fixed off-screen render surface for every queued job. Preview PNGs are always displayed via
// `object-fit: cover` in the node's <img> overlay (see MapNodeView), so an exact pixel match to
// each node's own (variable) DOM width isn't required for correctness — a single shared surface
// keeps the queue's engine reuse simple. 16:9 mirrors the `.pm-map-node__map-container` CSS ratio
// (`width:100%; aspect-ratio:16/9`).
//
// Sized generously (comfortably above typical report content-column widths, which run roughly
// 700-900px) rather than to a "typical" node width: downscaling a larger capture for display
// via object-fit is lossless-looking, whereas upscaling a smaller one would visibly soften
// basemap/tactical-graphic/icon detail. This keeps capture quality at parity with the old
// per-node engine (ticket 05 AC: "quality/content... unchanged... just produced by the shared
// engine"), which rendered directly into each node's own (variable, often wider-than-640px) DOM
// container instead of a small fixed surface.
const RENDER_WIDTH = 1280;
const RENDER_HEIGHT = 720;

function createHiddenContainer() {
    const el = document.createElement('div');
    el.className = 'map-preview-queue__hidden-surface';
    el.style.cssText = [
        'position:fixed',
        'left:-9999px',
        'top:-9999px',
        `width:${RENDER_WIDTH}px`,
        `height:${RENDER_HEIGHT}px`,
        'visibility:hidden',
        'pointer-events:none',
    ].join(';');
    document.body.appendChild(el);
    return el;
}

/**
 * A content/geometry-derived signature for a job's render inputs. Used to skip re-capturing and
 * re-persisting a snapshot when nothing has actually changed since the last successful write for
 * that nodeId (e.g. a plain scroll-into-view re-trigger with no edits).
 */
function computeSignature({ basemap, geojson, fitKey, gridType, overlayType, currentDtgMs, dtgSelectedMs }) {
    return [
        basemap || '', fitKey || '', gridType || '', overlayType || '', currentDtgMs ?? '', dtgSelectedMs ?? '',
        JSON.stringify(geojson?.features || []),
    ].join('|');
}

export class MapPreviewQueueService {
    /**
     * @param {object} [opts]
     * @param {MapPreviewEngine} [opts.engine] — injectable for tests; defaults to a real engine
     *   bound to a hidden DOM container.
     * @param {() => Promise<void>} [opts.ensureInitFn] — injectable for tests; defaults to the
     *   mil-sym-ts-web `ensureInit`.
     * @param {HTMLElement} [opts.container] — injectable for tests.
     */
    constructor(opts = {}) {
        this._container = opts.container || createHiddenContainer();
        this._engine = opts.engine || new MapPreviewEngine(this._container);
        const ensureInitFn = opts.ensureInitFn || ensureInit;
        this._initPromise = Promise.resolve()
            .then(() => ensureInitFn())
            .catch((err) => { console.warn('[MapPreviewQueueService] ensureInit failed', err); });

        /** @type {Map<string, object>} nodeId -> pending job params */
        this._jobs = new Map();
        /** @type {string[]} */
        this._order = [];
        this._processing = false;
        this._destroyed = false;
        this._activeNodeId = null;
        this._activeCancelled = false;
        /** @type {Map<string, string>} nodeId -> last-persisted signature, for redundant-write skip */
        this._lastSignature = new Map();
        // Ticket 05: nodeId -> human-readable failure message for the most recent *failed*
        // capture attempt. Cleared as soon as a later attempt for that nodeId succeeds (or is
        // cancelled), so a one-off failure doesn't linger as a permanent error once a retry
        // works.
        this._lastError = new Map();
    }

    /**
     * Enqueue (or update) a preview refresh request for a map-node.
     *
     * @param {object} job
     * @param {string} job.nodeId
     * @param {boolean} [job.priority] — true for a node that just became visible; jumps ahead of
     *   queued non-priority (background) jobs.
     * @param {boolean} [job.fitBounds] — force a view fit even if the bbox key hasn't changed.
     * @param {(nodeId: string) => import('yjs').XmlFragment|null} job.getFragment
     * @param {() => object} job.getAttrs — returns the map-node's *current* PM attrs (read at run
     *   time, not enqueue time, so a queued job always reflects the latest viewId etc.)
     * @param {import('yjs').Doc|null} job.ydoc
     * @param {import('yjs').Map|null} job.yMapPreviews
     */
    requestRefresh(job) {
        if (this._destroyed || !job?.nodeId) return;
        const { nodeId } = job;
        const existing = this._jobs.get(nodeId);
        if (existing) {
            existing.priority = existing.priority || !!job.priority;
            existing.fitBounds = existing.fitBounds || !!job.fitBounds;
            existing.getFragment = job.getFragment;
            existing.getAttrs = job.getAttrs;
            existing.ydoc = job.ydoc;
            existing.yMapPreviews = job.yMapPreviews;
        } else {
            this._jobs.set(nodeId, {
                priority: !!job.priority,
                fitBounds: !!job.fitBounds,
                getFragment: job.getFragment,
                getAttrs: job.getAttrs,
                ydoc: job.ydoc,
                yMapPreviews: job.yMapPreviews,
            });
            this._order.push(nodeId);
        }
        // Ticket 05: optimistically clear any stale failure the moment a fresh attempt is
        // enqueued (e.g. the user clicking the error badge's retry affordance) — getJobState()
        // immediately reflects 'queued'/'active' instead of a leftover 'error' from the previous
        // attempt while this new one is in flight; _runJob re-populates it if this attempt fails
        // too.
        this._lastError.delete(nodeId);
        this._drain();
    }

    /**
     * Remove a queued job for nodeId, or mark the in-flight job for nodeId (if it's the one
     * currently processing) so its result is discarded instead of persisted. Safe to call even if
     * no job exists for nodeId.
     */
    cancel(nodeId) {
        if (!nodeId) return;
        this._jobs.delete(nodeId);
        const idx = this._order.indexOf(nodeId);
        if (idx !== -1) this._order.splice(idx, 1);
        if (this._activeNodeId === nodeId) this._activeCancelled = true;
        this._lastSignature.delete(nodeId);
        this._lastError.delete(nodeId);
    }

    destroy() {
        this._destroyed = true;
        this._jobs.clear();
        this._order = [];
        this._lastSignature.clear();
        this._lastError.clear();
        this._engine.destroy();
        if (this._container?.isConnected) this._container.remove();
    }

    /**
     * Ticket 10: lets a consumer (MapNodeView) tell "sitting in the queue awaiting its turn"
     * apart from "the one job currently being rendered/captured" apart from "no job at all" for
     * a given nodeId, so the UI can show a queued-vs-idle indicator instead of the queue's FIFO/
     * priority serialization (see file header) looking like a stalled/broken refresh under load.
     *
     * A plain synchronous query rather than an event/subscription: this class has no pub/sub
     * today, and MapNodeView already drives its own refresh scheduling off
     * requestAnimationFrame (see `_scheduleRefresh`), so it polls this method on that same
     * cadence instead of this service growing a second, event-based notification mechanism for
     * one query.
     *
     * Ticket 05 extends this with an `'error'` state: once a job for nodeId fails (see
     * `_runJob`'s catch), `getJobState` reports `'error'` until either a new refresh is enqueued
     * for that nodeId (`requestRefresh` clears `_lastError` immediately) or the node itself is
     * cancelled (`cancel` clears it too). 'queued'/'active' always take precedence over a leftover
     * error from a *previous* attempt, so a manual retry's in-flight state is never masked by the
     * failure it's retrying.
     *
     * @param {string} nodeId
     * @returns {'queued'|'active'|'error'|null}
     */
    getJobState(nodeId) {
        if (!nodeId) return null;
        if (this._activeNodeId === nodeId) return 'active';
        if (this._jobs.has(nodeId)) return 'queued';
        if (this._lastError.has(nodeId)) return 'error';
        return null;
    }

    /**
     * Ticket 05: the human-readable reason the most recent attempt for nodeId failed, or null if
     * there's no outstanding failure (never failed, or a later attempt succeeded/was cancelled
     * since). Companion to `getJobState`'s `'error'` state — kept as a separate query rather than
     * folded into `getJobState`'s return value so a consumer can show *why* it failed without
     * `getJobState`'s signature growing an object return type used only for one state.
     *
     * @param {string} nodeId
     * @returns {string|null}
     */
    getJobError(nodeId) {
        if (!nodeId) return null;
        return this._lastError.get(nodeId) ?? null;
    }

    // ─── Internal ────────────────────────────────────────────────────────────

    _nextNodeId() {
        for (const id of this._order) {
            if (this._jobs.get(id)?.priority) return id;
        }
        return this._order.length > 0 ? this._order[0] : null;
    }

    _drain() {
        if (this._processing || this._destroyed) return;
        const nodeId = this._nextNodeId();
        if (nodeId == null) return;

        const job = this._jobs.get(nodeId);
        this._jobs.delete(nodeId);
        const idx = this._order.indexOf(nodeId);
        if (idx !== -1) this._order.splice(idx, 1);

        this._processing = true;
        this._activeNodeId = nodeId;
        this._activeCancelled = false;

        Promise.resolve()
            .then(() => this._runJob(nodeId, job))
            .catch((err) => {
                console.warn('[MapPreviewQueueService] job failed', nodeId, err);
                if (!this._destroyed && !this._activeCancelled) {
                    this._lastError.set(nodeId, err?.message || 'Preview render failed.');
                }
            })
            .then(() => {
                this._processing = false;
                this._activeNodeId = null;
                if (this._jobs.size > 0) this._drain();
            });
    }

    async _runJob(nodeId, job) {
        await this._initPromise;
        if (this._destroyed || this._activeCancelled) return;

        const fragment = job.getFragment ? job.getFragment(nodeId) : null;
        const previewState = readMapPreviewState(fragment);
        const attrs = job.getAttrs ? job.getAttrs() : {};
        const effectiveViewId = resolveActiveViewId(attrs);

        // Ticket 02: the map NODE's own persisted DTG selection (ticket 01's `dtgSelectedMs`
        // attr on the map node, read via `attrs` above) — a completely separate concept from
        // `currentDtgMs` below (that one drives satellite-imagery-as-of-date, not marking
        // resolution; shared-map-data ticket 03 moved it from the shared map's `map_meta` onto
        // this same per-node `attrs`, but the two remain unrelated concepts). `null`/`undefined`
        // (pre-ticket-01 documents, or a node where the user hasn't set a DTG yet) is forwarded
        // as-is: `resolveTrackAtDtg` (mapPmAdapter.js) treats `cursorDtg == null` as "show latest
        // known state", which is exactly today's (pre-fix) behavior — so existing embeds render
        // unchanged. Read before the `resolveViewBbox` call below (moved up from its original
        // position) so the "All markings" bbox fallback can resolve markings against the SAME
        // DTG the rendered features below use (bug fix: all-markings-dtg-crop) — see
        // resolveViewBbox/computeDefaultViewBbox's doc comments.
        const dtgSelectedMs = attrs?.dtgSelectedMs ?? null;
        const bbox = resolveViewBbox(effectiveViewId, previewState.views, previewState.markings, previewState.layers, dtgSelectedMs);
        const fitKey = viewFitKey(effectiveViewId, bbox);
        const geojson = {
            type: 'FeatureCollection',
            features: markingsToGeoJSON(previewState.markings, previewState.layers, dtgSelectedMs),
        };
        // Marking-unification (bug fix): expand tactical-line/-area/-point markings into their
        // full MIL-STD-2525 multipoint GeoJSON (arrowheads, echelon ticks, dashes, etc.) via the
        // SAME shared step useMapEditor.js's `syncTacticalSource` uses for the live editor — this
        // engine previously had no equivalent at all, so tactical markings fell through to the
        // generic Point/LineString/Polygon fallback in the rendered preview image. `viewportOpts`
        // mirrors `useMapEditor.js`'s `getViewportOpts(map)` shape (pixelWidth/pixelHeight/
        // viewportBbox) using this job's fixed render surface + resolved bbox in place of a live
        // OL map/view, which the preview path doesn't have.
        const viewportOpts = bbox
            ? { pixelWidth: RENDER_WIDTH, pixelHeight: RENDER_HEIGHT, viewportBbox: `${bbox[0][0]},${bbox[0][1]},${bbox[1][0]},${bbox[1][1]}` }
            : {};
        const tacticalGeojson = {
            type: 'FeatureCollection',
            features: expandTacticalMarkingsToFeatures(previewState.markings, viewportOpts, dtgSelectedMs).map((entry) => entry.feature),
        };
        const basemap = attrs?.basemapStyleId;
        const gridType = attrs?.activeGridId || null;
        // shared-map-data ticket 03: `overlayType`/`currentDtgMs` moved off the shared map doc's
        // `map_meta` node onto this mapNode's own attrs — each embed picks its own overlay/
        // satellite-cursor "window" independently, even though the underlying layers/markings are
        // shared. Read straight off `attrs` (mirrors `basemap`/`gridType`/`dtgSelectedMs` above)
        // rather than `previewState.meta`.
        const overlayType = attrs?.overlayType;
        const currentDtgMs = attrs?.currentDtgMs ?? null;

        const signature = computeSignature({ basemap, geojson, fitKey, gridType, overlayType, currentDtgMs, dtgSelectedMs });
        if (!job.fitBounds && this._lastSignature.get(nodeId) === signature) {
            // Nothing changed since the last successful capture for this node — skip the render +
            // Yjs write entirely (avoids broadcasting a no-op snapshot to every collaborator on
            // every plain scroll-into-view re-trigger).
            return;
        }

        // MINSKY-17028 follow-up (v2): `_lastSignature` above is per-instance and empty again on
        // every editor remount (including a WS reconnect), and MapNodeView's initial refresh
        // always sets fitBounds:true — which bypasses that in-memory check entirely. A prior fix
        // attempt tried comparing the freshly *rendered* dataUrl against what's already
        // persisted before writing, but live verification showed two independent
        // MapPreviewEngine instances render visibly-identical but byte-DIFFERENT PNGs for the
        // exact same input content (anti-aliasing / label-placement / tile-paint-order noise is
        // not guaranteed pixel-stable across separate OL Map/WebGL contexts) — so that comparison
        // still fired spurious writes. The render-INPUT signature computed above has no such
        // noise: it's derived from source data (geometry/basemap/grid/overlay/DTG), not pixels,
        // so comparing it against what's already PERSISTED (not just this instance's in-memory
        // cache) reliably detects "nothing changed" even from a brand-new instance, and lets us
        // skip the expensive render entirely (not just the write).
        const persistedSignature = job.yMapPreviews ? getMapPreviewSignature(job.yMapPreviews, nodeId) : '';
        if (job.yMapPreviews && persistedSignature && persistedSignature === signature) {
            this._lastSignature.set(nodeId, signature);
            return;
        }

        await this._engine.sync({
            basemap,
            geojson,
            tacticalGeojson,
            bbox,
            fitBounds: true,
            fitPadding: resolveViewFitPadding(effectiveViewId),
            gridType,
            overlayType,
            currentDtgMs,
        });
        if (this._destroyed || this._activeCancelled) return;

        const dataUrl = await this._engine.captureSnapshot();
        if (this._destroyed || this._activeCancelled) return;
        if (!dataUrl) {
            // Ticket 05: previously returned silently here, leaving MapNodeView's "Generating map
            // preview…" placeholder hanging forever with zero feedback on a real capture failure
            // (as opposed to the cancellation/destruction cases above, which are intentional
            // no-ops, not failures). Record it so `getJobState`/`getJobError` can surface a
            // distinct error state with a retry affordance instead.
            this._lastError.set(nodeId, 'Preview capture returned no image.');
            return;
        }

        if (job.ydoc && job.yMapPreviews) {
            // MINSKY-17028 follow-up (v2): the signature check above already returned early when
            // nothing changed (including across fresh instances/remounts), so reaching here means
            // a genuine content change (or no persisted signature yet, e.g. a pre-existing entry
            // from before this field existed) — always persist, and record the signature
            // alongside the preview so the NEXT instance (reconnect/remount) can skip without
            // re-rendering at all.
            setMapPreview(job.ydoc, job.yMapPreviews, nodeId, dataUrl, signature);
        }
        this._lastSignature.set(nodeId, signature);
        this._lastError.delete(nodeId);

        // Legend/chrome are driven independently by MapNodeView from its own MapFragmentStore
        // read of the same fragment; nothing further to do with previewState here.
    }
}
